/** @format */

import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Button,
    Menu,
    MenuItem,
    Typography,
    Chip,
    Divider,
    Tooltip,
} from "@mui/material";
import {
    DashboardOutlined,
    ReceiptLongOutlined,
    PeopleAltOutlined,
    CampaignOutlined,
    DescriptionOutlined,
    HistoryOutlined,
    SettingsOutlined,
    CreditCardOutlined,
    ExpandMore,
    BusinessOutlined,
    Check,
    AddOutlined,
    PriorityHighOutlined,
    MarkEmailReadOutlined,
    AssignmentOutlined,
    RuleOutlined,
    GavelOutlined,
    MenuOutlined,
    HandshakeOutlined,
    AutoAwesomeOutlined,
    InsightsOutlined,
    LogoutOutlined,
    AccountCircleOutlined,
    AdminPanelSettingsOutlined,
    SupportAgentOutlined,
    InfoOutlined,
} from "@mui/icons-material";
import { AppDispatch } from "../../redux/store/store";
import {
    setActiveBusiness,
    setPlatformSuperAdmin,
    enterImpersonation,
    exitImpersonation,
} from "../../redux/slices/collectSlice";
import { logoutBusinessUser } from "../../redux/slices/businessAuthSlice";
import {
    fetchMyBusinesses,
    fetchActiveBusiness,
} from "../../redux/actions/collectAction";
import { fetchCollectSubscription } from "../../redux/actions/collectBillingAction";
import {
    selectActiveBusinessId,
    selectActiveBusiness,
    selectActiveBusinessRole,
    selectBusinessMemberships,
    selectIsPlatformSuperAdmin,
    selectImpersonation,
} from "../../redux/selectors/collectSelector";
import {
    selectCollectAccess,
    selectCollectHasAccess,
} from "../../redux/selectors/collectBillingSelector";
import {
    submitGetAdminMe,
    submitGetCollectSystemInfo,
} from "../../redux/services/collectService";
import {
    readStoredImpersonation,
    clearStoredImpersonation,
} from "./admin/impersonationStorage";
import { colors } from "../../utility/types/constant";
import {
    ICollectSystemInfo,
    QuickBooksEnvironment,
} from "../../utility/types/collectTypes";
import CollectMyAccountModal from "./CollectMyAccountModal";
import packageJson from "../../../package.json";

// ─────────────────────────────────────────────────────────────────────────
// CollectLayout — application shell for the Collect (AARRP) workspace.
// Renders a fixed left side panel with primary navigation + workspace
// switcher on desktop, and a collapsible drawer behind a top app bar on
// mobile. Page content renders through <Outlet />.
// ─────────────────────────────────────────────────────────────────────────

const DRAWER_WIDTH = 264;
const APP_VERSION = packageJson.version;
const BUILD_SHA = process.env.REACT_APP_BUILD_SHA?.slice(0, 7) || "local";

const ENVIRONMENT_PRESENTATION: Record<
    QuickBooksEnvironment,
    { label: string; backgroundColor: string; color: string }
> = {
    sandbox: {
        label: "Sandbox",
        backgroundColor: "#fef3c7",
        color: "#92400e",
    },
    production: {
        label: "Production",
        backgroundColor: "#fee2e2",
        color: "#991b1b",
    },
    unknown: {
        label: "Unknown",
        backgroundColor: "#e5e7eb",
        color: "#4b5563",
    },
};

// Collect surfaces a lapsed/expired business is still allowed to reach (mirrors
// the backend exemption list in collectAccessService.isCollectAccessExempt):
// the read-only Dashboard teaser plus every billing path so they can pay. Any
// other /collect/* route is premium and redirects to the dashboard when the
// subscription isn't active — the backend still 403s the underlying data, this
// just prevents the gated page shell from flashing before that redirect.
const LAPSED_ALLOWED_PATHS = new Set<string>([
    "/collect",
    "/collect/dashboard",
    "/collect/billing",
    "/collect/subscribe",
    "/collect/support",
]);

interface NavItem {
    label: string;
    path: string;
    icon: React.ReactNode;
    matchPrefix: string;
}

const NAV_ITEMS: NavItem[] = [
    {
        label: "Dashboard",
        path: "/collect/dashboard",
        icon: <DashboardOutlined fontSize="small" />,
        matchPrefix: "/collect/dashboard",
    },
    {
        label: "Analytics",
        path: "/collect/analytics",
        icon: <InsightsOutlined fontSize="small" />,
        matchPrefix: "/collect/analytics",
    },
    {
        label: "Invoices",
        path: "/collect/invoices",
        icon: <ReceiptLongOutlined fontSize="small" />,
        matchPrefix: "/collect/invoices",
    },
    {
        label: "Customers",
        path: "/collect/customers",
        icon: <PeopleAltOutlined fontSize="small" />,
        matchPrefix: "/collect/customers",
    },
    {
        label: "Recovery",
        path: "/collect/recovery",
        icon: <PriorityHighOutlined fontSize="small" />,
        matchPrefix: "/collect/recovery",
    },
    {
        label: "Promises",
        path: "/collect/promises",
        icon: <HandshakeOutlined fontSize="small" />,
        matchPrefix: "/collect/promises",
    },
    {
        label: "Replies",
        path: "/collect/replies",
        icon: <MarkEmailReadOutlined fontSize="small" />,
        matchPrefix: "/collect/replies",
    },
    {
        label: "Tasks",
        path: "/collect/tasks",
        icon: <AssignmentOutlined fontSize="small" />,
        matchPrefix: "/collect/tasks",
    },
    {
        label: "Review Queue",
        path: "/collect/review",
        icon: <RuleOutlined fontSize="small" />,
        matchPrefix: "/collect/review",
    },
    {
        label: "Legal Action",
        path: "/collect/legal-action",
        icon: <GavelOutlined fontSize="small" />,
        matchPrefix: "/collect/legal-action",
    },
    {
        label: "Campaigns",
        path: "/collect/campaigns",
        icon: <CampaignOutlined fontSize="small" />,
        matchPrefix: "/collect/campaigns",
    },
    {
        label: "Templates",
        path: "/collect/templates",
        icon: <DescriptionOutlined fontSize="small" />,
        matchPrefix: "/collect/templates",
    },
    {
        label: "Recommendations",
        path: "/collect/recommendations",
        icon: <AutoAwesomeOutlined fontSize="small" />,
        matchPrefix: "/collect/recommendations",
    },
    {
        label: "Predictions",
        path: "/collect/predictions",
        icon: <InsightsOutlined fontSize="small" />,
        matchPrefix: "/collect/predictions",
    },
    {
        label: "Team",
        path: "/collect/team",
        icon: <PeopleAltOutlined fontSize="small" />,
        matchPrefix: "/collect/team",
    },
    {
        label: "Activity",
        path: "/collect/activity",
        icon: <HistoryOutlined fontSize="small" />,
        matchPrefix: "/collect/activity",
    },
    {
        label: "Settings",
        path: "/collect/settings",
        icon: <SettingsOutlined fontSize="small" />,
        matchPrefix: "/collect/settings",
    },
    {
        label: "Help & Support",
        path: "/collect/support",
        icon: <SupportAgentOutlined fontSize="small" />,
        matchPrefix: "/collect/support",
    },
    {
        label: "Billing",
        path: "/collect/billing",
        icon: <CreditCardOutlined fontSize="small" />,
        matchPrefix: "/collect/billing",
    },
];

// Super-admin-only console entry. Kept out of NAV_ITEMS so it never leaks into
// role/access filtering; it's prepended explicitly for platform super admins.
const ADMIN_NAV_ITEM: NavItem = {
    label: "Admin",
    path: "/collect/admin",
    icon: <AdminPanelSettingsOutlined fontSize="small" />,
    matchPrefix: "/collect/admin",
};

// ─────────────────────────────────────────────────────────────────────────
// Sidebar — brand header, primary nav list, workspace switcher footer.
// ─────────────────────────────────────────────────────────────────────────
interface SidebarProps {
    onNavigate?: () => void;
    systemInfo: ICollectSystemInfo | null;
}

const Sidebar: React.FC<SidebarProps> = ({ onNavigate, systemInfo }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const role = useSelector(selectActiveBusinessRole);
    const hasAccess = useSelector(selectCollectHasAccess);
    const isPlatformSuperAdmin = useSelector(selectIsPlatformSuperAdmin);
    const [accountOpen, setAccountOpen] = useState(false);
    const environment =
        ENVIRONMENT_PRESENTATION[
        systemInfo?.quickBooksEnvironment || "unknown"
        ] || ENVIRONMENT_PRESENTATION.unknown;

    // Settings is owner/admin only — staff never see it in the nav.
    const isAdminOrOwner = role === "owner" || role === "admin";

    const restrictedPath = [
        "/collect/settings",
        "/collect/team",
        "/collect/campaigns",
        "/collect/templates",
        "/collect/legal-action",
        "/collect/billing"
    ]

    const roleScopedItems = isAdminOrOwner
        ? NAV_ITEMS
        : NAV_ITEMS.filter((item) => !restrictedPath.some((path) => item.path.includes(path)));

    // Story 5 — when the business has no active access (trial expired / lapsed
    // subscription and not a super-admin), collapse the nav to just Dashboard
    // and Billing so the only paths forward are to view status or subscribe.
    const baseNavItems = hasAccess
        ? roleScopedItems
        : NAV_ITEMS.filter(
            (item) =>
                item.path === "/collect/dashboard" ||
                item.path === "/collect/support" ||
                item.path === "/collect/billing",
        );

    // Platform super admins always get the Admin console entry, pinned to the
    // top and independent of the active business's subscription state. Server
    // authorization is authoritative; this is convenience gating only.
    const navItems = isPlatformSuperAdmin
        ? [ADMIN_NAV_ITEM, ...baseNavItems]
        : baseNavItems;


    const handleSignOut = async () => {
        await dispatch(logoutBusinessUser());
        navigate("/collect/sign-in");
        onNavigate?.();
    };

    // Longest matching prefix wins so /collect/invoices/123 keeps Invoices lit.
    const activeIndex = (() => {
        const matches = navItems.map((item, idx) =>
            location.pathname.startsWith(item.matchPrefix) ? idx : -1,
        ).filter((i) => i >= 0);
        return matches.length > 0 ? matches[matches.length - 1] : 0;
    })();

    const go = (path: string) => {
        navigate(path);
        onNavigate?.();
    };

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
            }}
        >
            {/* Brand */}
            <Box
                sx={{
                    px: 2.5,
                    py: 2.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                }}
            >
                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "10px",
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.blueRibbon} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: "1rem",
                            color: "#fff",
                            lineHeight: 1,
                        }}
                    >
                        F
                    </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        noWrap
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: "0.98rem",
                            color: colors.primary,
                            lineHeight: 1.1,
                        }}
                    >
                        FundsyFi
                    </Typography>
                    <Typography
                        noWrap
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.66rem",
                            color: colors.mainSecondary,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                        }}
                    >
                        Recover
                    </Typography>
                </Box>
            </Box>

            {/* Primary nav */}
            <List
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    px: 1.25,
                    py: 1.5,
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(15, 23, 42, 0.12)",
                        borderRadius: 3,
                    },
                }}
            >
                {navItems.map((item, idx) => {
                    const active = idx === activeIndex;
                    return (
                        <ListItemButton
                            key={item.path}
                            onClick={() => go(item.path)}
                            sx={{
                                borderRadius: "10px",
                                mb: 0.5,
                                px: 1.5,
                                py: 1,
                                position: "relative",
                                backgroundColor: active
                                    ? "rgba(30, 193, 149, 0.12)"
                                    : "transparent",
                                "&:hover": {
                                    backgroundColor: active
                                        ? "rgba(30, 193, 149, 0.16)"
                                        : "rgba(15, 23, 42, 0.04)",
                                },
                                "&::before": active
                                    ? {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: "20%",
                                        bottom: "20%",
                                        width: 3,
                                        borderRadius: "0 3px 3px 0",
                                        backgroundColor: colors.mainSecondary,
                                    }
                                    : undefined,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 34,
                                    color: active ? colors.primary : "#6b7280",
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    sx: {
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: active ? 700 : 600,
                                        fontSize: "0.85rem",
                                        color: active ? colors.primary : "#374151",
                                    },
                                }}
                            />
                        </ListItemButton>
                    );
                })}
            </List>

            {/* Workspace switcher */}
            <Box
                sx={{
                    p: 1.5,
                    borderTop: "1px solid rgba(15, 23, 42, 0.06)",
                }}
            >
                <WorkspaceSwitcher onNavigate={onNavigate} />
                <Tooltip
                    placement="right"
                    arrow
                    title={
                        <Box sx={{ py: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                                FundsyFi Recover
                            </Typography>
                            <Typography sx={{ mt: 0.5, fontSize: "0.74rem" }}>
                                QuickBooks: {environment.label}
                            </Typography>
                            <Typography sx={{ fontSize: "0.74rem" }}>
                                App version: {APP_VERSION}
                            </Typography>
                            <Typography sx={{ fontSize: "0.74rem" }}>
                                Build: {BUILD_SHA}
                            </Typography>
                        </Box>
                    }
                >
                    <ListItemButton
                        component="div"
                        aria-label={`System information: QuickBooks ${environment.label}, app version ${APP_VERSION}, build ${BUILD_SHA}`}
                        sx={{
                            mt: 1,
                            borderRadius: "10px",
                            py: 0.75,
                            px: 1.5,
                            cursor: "help",
                            "&:hover": {
                                backgroundColor: "rgba(15, 23, 42, 0.04)",
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 34, color: "#6b7280" }}>
                            <InfoOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary="System"
                            secondary={`v${APP_VERSION}`}
                            primaryTypographyProps={{
                                sx: {
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 600,
                                    fontSize: "0.82rem",
                                    color: "#374151",
                                },
                            }}
                            secondaryTypographyProps={{
                                sx: {
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.68rem",
                                    color: "#6b7280",
                                },
                            }}
                        />
                        <Chip
                            size="small"
                            label={environment.label}
                            sx={{
                                height: 22,
                                ml: 0.5,
                                backgroundColor: environment.backgroundColor,
                                color: environment.color,
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.62rem",
                            }}
                        />
                    </ListItemButton>
                </Tooltip>
                <ListItemButton
                    onClick={() => setAccountOpen(true)}
                    sx={{
                        mt: 1,
                        borderRadius: "10px",
                        py: 1,
                        px: 1.5,
                        "&:hover": {
                            backgroundColor: "rgba(15, 23, 42, 0.04)",
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{ minWidth: 34, color: colors.primary }}
                    >
                        <AccountCircleOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="My Account"
                        primaryTypographyProps={{
                            sx: {
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "#374151",
                            },
                        }}
                    />
                </ListItemButton>
                <ListItemButton
                    onClick={handleSignOut}
                    sx={{
                        mt: 1,
                        borderRadius: "10px",
                        py: 1,
                        px: 1.5,
                        "&:hover": {
                            backgroundColor: "rgba(239, 68, 68, 0.08)",
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 34, color: "#ef4444" }}>
                        <LogoutOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Sign out"
                        primaryTypographyProps={{
                            sx: {
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "#ef4444",
                            },
                        }}
                    />
                </ListItemButton>
            </Box>
            <CollectMyAccountModal
                open={accountOpen}
                onClose={() => setAccountOpen(false)}
                businessId={activeBusinessId}
            />
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────
// WorkspaceSwitcher — full-width dropdown showing the active business name +
// role chip; lets the user switch between any workspace they belong to.
// ─────────────────────────────────────────────────────────────────────────
const WorkspaceSwitcher: React.FC<{ onNavigate?: () => void }> = ({
    onNavigate,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const activeBusiness = useSelector(selectActiveBusiness);
    const memberships = useSelector(selectBusinessMemberships);
    const isPlatformSuperAdmin = useSelector(selectIsPlatformSuperAdmin);
    const impersonation = useSelector(selectImpersonation);

    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const open = Boolean(anchor);

    // Platform super admins have no default workspace. The switcher stays empty
    // until they open a business from the Admin console (impersonation); then it
    // shows only that business, with a shortcut back to the console.
    if (isPlatformSuperAdmin) {
        if (!impersonation.active) return null;
        const adminLabel =
            activeBusiness?.name || impersonation.businessName || "Workspace";
        return (
            <>
                <Button
                    fullWidth
                    onClick={(e) => setAnchor(e.currentTarget)}
                    startIcon={<BusinessOutlined fontSize="small" />}
                    endIcon={<ExpandMore fontSize="small" />}
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        color: colors.primary,
                        justifyContent: "space-between",
                        border: "1px solid rgba(15, 23, 42, 0.1)",
                        borderRadius: "10px",
                        px: 1.5,
                        py: 1,
                        "& .MuiButton-startIcon": { mr: 0.75 },
                        "&:hover": {
                            backgroundColor: "rgba(15, 23, 42, 0.04)",
                            borderColor: "rgba(15, 23, 42, 0.2)",
                        },
                    }}
                >
                    <Typography
                        noWrap
                        sx={{
                            flex: 1,
                            textAlign: "left",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                        }}
                    >
                        {adminLabel}
                    </Typography>
                </Button>
                <Menu
                    anchorEl={anchor}
                    open={open}
                    onClose={() => setAnchor(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                    PaperProps={{
                        sx: {
                            minWidth: 260,
                            borderRadius: "12px",
                            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                        },
                    }}
                >
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                color: "#6b7280",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Current workspace
                        </Typography>
                    </Box>
                    <MenuItem
                        onClick={() => setAnchor(null)}
                        sx={{ py: 1.25, px: 2, gap: 1, alignItems: "center" }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 18,
                            }}
                        >
                            <Check
                                fontSize="small"
                                sx={{ color: colors.mainSecondary }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                noWrap
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 600,
                                    fontSize: "0.88rem",
                                    color: "#1f2937",
                                }}
                            >
                                {adminLabel}
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            label="admin"
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                height: 20,
                                backgroundColor: "rgba(59, 130, 246, 0.12)",
                                color: "#1e40af",
                            }}
                        />
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                        onClick={() => {
                            setAnchor(null);
                            navigate("/collect/admin");
                            onNavigate?.();
                        }}
                        sx={{ py: 1.25, px: 2, gap: 1 }}
                    >
                        <BusinessOutlined
                            fontSize="small"
                            sx={{ color: "#6b7280" }}
                        />
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "#1f2937",
                            }}
                        >
                            Choose another business
                        </Typography>
                    </MenuItem>
                </Menu>
            </>
        );
    }

    if (!activeBusinessId || !memberships || memberships.length === 0) {
        return null;
    }

    const label = activeBusiness?.name || "Workspace";

    const onPick = (businessId: string) => {
        setAnchor(null);
        if (businessId !== activeBusinessId) {
            dispatch(setActiveBusiness(businessId));
            // Re-hydrate the business + role for the workspace we just switched
            // to so role-gated actions (e.g. Connect QuickBooks) stay visible.
            dispatch(fetchActiveBusiness(businessId));
            navigate("/collect/dashboard");
            onNavigate?.();
        }
    };

    return (
        <>
            <Button
                fullWidth
                onClick={(e) => setAnchor(e.currentTarget)}
                startIcon={<BusinessOutlined fontSize="small" />}
                endIcon={<ExpandMore fontSize="small" />}
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    textTransform: "none",
                    color: colors.primary,
                    justifyContent: "space-between",
                    border: "1px solid rgba(15, 23, 42, 0.1)",
                    borderRadius: "10px",
                    px: 1.5,
                    py: 1,
                    "& .MuiButton-startIcon": { mr: 0.75 },
                    "&:hover": {
                        backgroundColor: "rgba(15, 23, 42, 0.04)",
                        borderColor: "rgba(15, 23, 42, 0.2)",
                    },
                }}
            >
                <Typography
                    noWrap
                    sx={{
                        flex: 1,
                        textAlign: "left",
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        fontWeight: "inherit",
                    }}
                >
                    {label}
                </Typography>
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                PaperProps={{
                    sx: {
                        minWidth: 260,
                        borderRadius: "12px",
                        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                    },
                }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                        }}
                    >
                        Switch workspace
                    </Typography>
                </Box>
                {memberships.map((m) => {
                    const isActive = m.businessId === activeBusinessId;
                    const name = m.business?.name || "Untitled workspace";
                    return (
                        <MenuItem
                            key={m._id}
                            onClick={() => onPick(m.businessId)}
                            sx={{
                                py: 1.25,
                                px: 2,
                                gap: 1,
                                alignItems: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 18,
                                }}
                            >
                                {isActive && (
                                    <Check
                                        fontSize="small"
                                        sx={{ color: colors.mainSecondary }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    noWrap
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 600,
                                        fontSize: "0.88rem",
                                        color: "#1f2937",
                                    }}
                                >
                                    {name}
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                label={m.role}
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    height: 20,
                                    backgroundColor:
                                        m.role === "owner"
                                            ? "rgba(30, 193, 149, 0.14)"
                                            : "rgba(59, 130, 246, 0.12)",
                                    color: m.role === "owner" ? "#0f7a5b" : "#1e40af",
                                }}
                            />
                        </MenuItem>
                    );
                })}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                    onClick={() => {
                        setAnchor(null);
                        navigate("/collect/setup");
                        onNavigate?.();
                    }}
                    sx={{ py: 1.25, px: 2, gap: 1 }}
                >
                    <AddOutlined fontSize="small" sx={{ color: "#6b7280" }} />
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            color: "#1f2937",
                        }}
                    >
                        Create new workspace
                    </Typography>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                    onClick={async () => {
                        setAnchor(null);
                        await dispatch(logoutBusinessUser());
                        navigate("/collect/sign-in");
                        onNavigate?.();
                    }}
                    sx={{ py: 1.25, px: 2, gap: 1 }}
                >
                    <LogoutOutlined fontSize="small" sx={{ color: "#ef4444" }} />
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            color: "#ef4444",
                        }}
                    >
                        Sign out
                    </Typography>
                </MenuItem>
            </Menu>
        </>
    );
};

const CollectLayout: React.FC = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [systemInfo, setSystemInfo] = useState<ICollectSystemInfo | null>(null);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const activeBusinessRole = useSelector(selectActiveBusinessRole);
    const memberships = useSelector(selectBusinessMemberships);
    const access = useSelector(selectCollectAccess);
    const impersonation = useSelector(selectImpersonation);
    const location = useLocation();

    // Keep the membership list available app-wide (needed by the workspace
    // switcher and role-gated actions).
    useEffect(() => {
        if (memberships.length === 0) {
            dispatch(fetchMyBusinesses());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Resolve the platform super-admin flag once (independent of any active
    // business) so the Admin console entry can render. If a super admin was
    // impersonating a business, restore that context after a full refresh
    // (Redux state isn't persisted; the target id lives in localStorage).
    useEffect(() => {
        let mounted = true;
        submitGetAdminMe().then((isAdmin) => {
            if (!mounted) return;
            dispatch(setPlatformSuperAdmin(isAdmin));
            if (isAdmin) {
                const stored = readStoredImpersonation();
                if (stored) {
                    dispatch(
                        enterImpersonation({
                            businessId: stored.businessId,
                            businessName: stored.businessName,
                        }),
                    );
                    dispatch(fetchActiveBusiness(stored.businessId));
                }
            } else {
                // A non-admin must never carry a stale impersonation marker.
                clearStoredImpersonation();
            }
        });
        return () => {
            mounted = false;
        };
    }, [dispatch]);

    useEffect(() => {
        let mounted = true;
        submitGetCollectSystemInfo()
            .then((info) => {
                if (mounted) setSystemInfo(info);
            })
            .catch(() => {
                if (mounted) setSystemInfo(null);
            });
        return () => {
            mounted = false;
        };
    }, []);

    // Whenever the active workspace changes (including a switch that clears
    // the cached business + role), re-hydrate the authoritative business and
    // role for the active tenant. Without this, switching workspaces leaves
    // the role null, which hides owner/admin actions like "Connect QuickBooks"
    // and renders "Workspace: —".
    useEffect(() => {
        if (activeBusinessId && !activeBusinessRole) {
            dispatch(fetchActiveBusiness(undefined));
        }
    }, [dispatch, activeBusinessId, activeBusinessRole]);

    // Story 5 — load the authoritative access summary (trial / subscription /
    // super-admin) whenever the active workspace changes, so the nav gate and
    // trial banner reflect the current tenant.
    useEffect(() => {
        if (activeBusinessId) {
            dispatch(fetchCollectSubscription(activeBusinessId));
        }
    }, [dispatch, activeBusinessId]);

    // Trial countdown / lapsed-access banner. Suppressed for super-admins and
    // for fully active subscriptions.
    const showTrialBanner =
        !!access &&
        !access.isSuperAdmin &&
        access.isTrialing &&
        access.hasAccess;
    const showLapsedBanner =
        !!access && !access.isSuperAdmin && !access.hasAccess;

    // Route-level access guard. Once we have an authoritative access summary and
    // the business is lapsed (not a super-admin), redirect any premium route to
    // the Dashboard so the gated page never renders. We only redirect after
    // `access` has loaded to avoid bouncing users during the initial fetch.
    const currentPath = location.pathname.replace(/\/+$/, "") || "/collect";
    const blockedWhenLapsed =
        showLapsedBanner && !LAPSED_ALLOWED_PATHS.has(currentPath);

    // Exit impersonation: clear the persisted marker + Redux context, then send
    // the admin back to the console (their own memberships re-hydrate there).
    const handleExitImpersonation = () => {
        clearStoredImpersonation();
        dispatch(exitImpersonation());
        dispatch(fetchMyBusinesses());
        navigate("/collect/admin");
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f6f8fb" }}>
            {/* ── Mobile top bar ─────────────────────────────────────── */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    display: { xs: "flex", md: "none" },
                    backgroundColor: "#ffffff",
                    color: colors.primary,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
                }}
            >
                <Toolbar sx={{ minHeight: 56, gap: 1 }}>
                    <IconButton
                        edge="start"
                        onClick={() => setMobileOpen(true)}
                        sx={{ color: colors.primary }}
                        aria-label="Open navigation"
                    >
                        <MenuOutlined />
                    </IconButton>
                    <Box
                        sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "9px",
                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.blueRibbon} 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "0.9rem",
                                color: "#fff",
                                lineHeight: 1,
                            }}
                        >
                            F
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: "1rem",
                            color: colors.primary,
                        }}
                    >
                        Recover
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* ── Navigation drawers ─────────────────────────────────── */}
            <Box
                component="nav"
                sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
            >
                {/* Mobile: temporary */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: "block", md: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: DRAWER_WIDTH,
                            borderRight: "1px solid rgba(15, 23, 42, 0.06)",
                        },
                    }}
                >
                    <Sidebar
                        onNavigate={() => setMobileOpen(false)}
                        systemInfo={systemInfo}
                    />
                </Drawer>

                {/* Desktop: permanent */}
                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        display: { xs: "none", md: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: DRAWER_WIDTH,
                            borderRight: "1px solid rgba(15, 23, 42, 0.06)",
                        },
                    }}
                >
                    <Sidebar systemInfo={systemInfo} />
                </Drawer>
            </Box>

            {/* ── Main content ───────────────────────────────────────── */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                    mt: { xs: "56px", md: 0 },
                }}
            >
                {impersonation.active && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            px: { xs: 2, md: 3 },
                            py: 1.25,
                            backgroundColor: "rgba(37, 99, 235, 0.12)",
                            borderBottom: "1px solid rgba(37, 99, 235, 0.3)",
                        }}
                    >
                        <Typography
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "#1e3a8a",
                            }}
                        >
                            <AdminPanelSettingsOutlined fontSize="small" />
                            Viewing{" "}
                            <strong>
                                {impersonation.businessName || "business"}
                            </strong>{" "}
                            as super admin, changes you make apply to this
                            account.
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LogoutOutlined />}
                            onClick={handleExitImpersonation}
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                textTransform: "none",
                                borderColor: "#1e3a8a",
                                color: "#1e3a8a",
                                "&:hover": {
                                    borderColor: "#1e3a8a",
                                    backgroundColor: "rgba(37, 99, 235, 0.08)",
                                },
                            }}
                        >
                            Exit
                        </Button>
                    </Box>
                )}
                {showTrialBanner && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            px: { xs: 2, md: 3 },
                            py: 1.25,
                            backgroundColor: "rgba(245, 158, 11, 0.12)",
                            borderBottom: "1px solid rgba(245, 158, 11, 0.28)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "#92400e",
                            }}
                        >
                            {access?.trialDaysRemaining === 0
                                ? "Your free trial ends today."
                                : `${access?.trialDaysRemaining} day${access?.trialDaysRemaining === 1 ? "" : "s"
                                } left in your free trial.`}{" "}
                            Subscribe to keep full access to Collect.
                        </Typography>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate("/collect/subscribe")}
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                textTransform: "none",
                                backgroundColor: colors.mainSecondary,
                                "&:hover": { backgroundColor: "#17a883" },
                            }}
                        >
                            Subscribe
                        </Button>
                    </Box>
                )}
                {showLapsedBanner && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            px: { xs: 2, md: 3 },
                            py: 1.25,
                            backgroundColor: "rgba(239, 68, 68, 0.1)",
                            borderBottom: "1px solid rgba(239, 68, 68, 0.26)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "#991b1b",
                            }}
                        >
                            {access?.state === "trial_expired"
                                ? "Your free trial has ended."
                                : access?.state === "past_due_expired"
                                    ? "Limited access, your subscription payment didn't go through."
                                    : "Your Collect subscription is inactive."}{" "}
                            {access?.state === "past_due_expired"
                                ? "Settle your subscription to restore full access."
                                : "Subscribe to restore full access."}
                        </Typography>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate("/collect/subscribe")}
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                textTransform: "none",
                                backgroundColor: colors.mainSecondary,
                                "&:hover": { backgroundColor: "#17a883" },
                            }}
                        >
                            {access?.state === "past_due_expired"
                                ? "Pay now"
                                : "Subscribe"}
                        </Button>
                    </Box>
                )}
                {blockedWhenLapsed ? (
                    <Navigate to="/collect/dashboard" replace />
                ) : (
                    <Outlet />
                )}
            </Box>
        </Box>
    );
};

export default CollectLayout;
